// ─────────────────────────────────────────────────────────────────────────
// Quiz score calendar — a small reusable month-grid, plain DOM (innerHTML +
// querySelector + addEventListener) matching the rest of the codebase's
// view style (see views/school.js). Used for a learner's own history
// (views/scores.js), a school owner's read-only look at one student's
// history (views/schoolStudentScores.js), and a school owner's aggregate
// view across every linked student (views/schoolScores.js) — the component
// itself has no notion of ownership, it's just handed a flat list of
// attempts.
//
// `attempts` — array of { id, courseTitle, topicTitle, score, maxScore,
// dateKey ('YYYY-MM-DD'), createdAt (ms), studentEmail?, studentName? }, as
// returned by lib/quizScore.js's
// getMyQuizAttempts/getStudentQuizHistory/getSchoolQuizLog.
// `groupByStudent` (default false) changes the day-detail panel from a flat
// chronological list of attempts into one collapsed entry per quiz (course +
// topic) that expands on click to show exactly who took it and how they
// scored — pass true only for the multi-student aggregate view.
// ─────────────────────────────────────────────────────────────────────────

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function pctColor(pct) {
  if (pct >= 80) return 'var(--success)';
  if (pct >= 50) return 'var(--accent)';
  return 'var(--danger)';
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function renderCalendarHistory(container, { attempts, groupByStudent = false }) {
  const byDate = new Map();
  (attempts || []).forEach((a) => {
    if (!a.dateKey) return;
    if (!byDate.has(a.dateKey)) byDate.set(a.dateKey, []);
    byDate.get(a.dateKey).push(a);
  });

  const mostRecentKey = (attempts || []).map((a) => a.dateKey).filter(Boolean).sort().pop();
  const initial = mostRecentKey ? new Date(`${mostRecentKey}T00:00:00Z`) : new Date();
  let viewYear = initial.getUTCFullYear();
  let viewMonth = initial.getUTCMonth(); // 0-11
  let selectedKey = mostRecentKey || null;

  container.innerHTML = `
    <div class="cal-history">
      <div class="cal-header">
        <button class="btn btn-sm" id="cal-prev">←</button>
        <strong id="cal-month-label" style="min-width:160px;text-align:center;"></strong>
        <button class="btn btn-sm" id="cal-next">→</button>
      </div>
      <div class="cal-grid" id="cal-grid"></div>
      <div class="settings-panel" id="cal-day-detail" style="margin-top:16px;"></div>
    </div>
  `;

  const monthLabel = container.querySelector('#cal-month-label');
  const grid = container.querySelector('#cal-grid');
  const dayDetail = container.querySelector('#cal-day-detail');

  function dateKeyFor(y, m, d) {
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  function renderDayDetail(key) {
    selectedKey = key;
    const dayAttempts = byDate.get(key) || [];
    if (!key || dayAttempts.length === 0) {
      dayDetail.innerHTML = `<div class="empty-state"><p>${key ? 'No quiz attempts on this day.' : 'Select a day with a score to see attempts.'}</p></div>`;
      return;
    }
    const dateLabel = new Date(`${key}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    if (groupByStudent) {
      // One collapsed entry per quiz (course + topic) — clicking it reveals
      // exactly who took THAT quiz and how they scored, instead of one row
      // per attempt with a repeated course/topic on every line.
      const byQuiz = new Map(); // topicId (or a title fallback) -> { courseTitle, topicTitle, participants }
      dayAttempts.forEach((a) => {
        const quizKey = a.topicId || `${a.courseTitle}::${a.topicTitle}`;
        if (!byQuiz.has(quizKey)) byQuiz.set(quizKey, { courseTitle: a.courseTitle || '', topicTitle: a.topicTitle || '', participants: [] });
        byQuiz.get(quizKey).participants.push(a);
      });
      const quizzes = [...byQuiz.values()].sort((a, b) => (
        a.courseTitle.localeCompare(b.courseTitle) || a.topicTitle.localeCompare(b.topicTitle)
      ));

      dayDetail.innerHTML = `
        <h3 style="margin-top:0;">${escapeHtml(dateLabel)}</h3>
        ${quizzes.map((q) => {
          const participants = q.participants.slice().sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          return `
            <details class="cal-quiz-group" style="margin-bottom:8px;">
              <summary style="cursor:pointer;padding:8px 0;font-weight:600;">${escapeHtml(q.courseTitle)} — ${escapeHtml(q.topicTitle)} <span style="color:var(--text-dim);font-weight:400;">(${participants.length} participant${participants.length === 1 ? '' : 's'})</span></summary>
              <div style="overflow:auto;"><table class="admin-table">
                <thead><tr><th>Student</th><th>Time</th><th style="text-align:right;">Score</th></tr></thead>
                <tbody>
                  ${participants.map((a) => {
                    const pct = a.maxScore > 0 ? Math.round((a.score / a.maxScore) * 100) : 0;
                    const studentLabel = a.studentName
                      ? `${escapeHtml(a.studentName)} <span style="color:var(--text-dim);font-size:0.85em;">${escapeHtml(a.studentEmail || '')}</span>`
                      : escapeHtml(a.studentEmail || a.studentUid || 'Unknown');
                    return `<tr>
                      <td>${studentLabel}</td>
                      <td>${a.createdAt ? new Date(a.createdAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : ''}</td>
                      <td style="text-align:right;font-weight:700;color:${pctColor(pct)};">${a.score}/${a.maxScore} (${pct}%)</td>
                    </tr>`;
                  }).join('')}
                </tbody>
              </table></div>
            </details>
          `;
        }).join('')}
      `;
      return;
    }

    const sortedAttempts = dayAttempts.slice().sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    dayDetail.innerHTML = `
      <h3 style="margin-top:0;">${escapeHtml(dateLabel)}</h3>
      <div style="overflow:auto;"><table class="admin-table">
        <thead><tr><th>Time</th><th>Course</th><th>Topic</th><th style="text-align:right;">Score</th></tr></thead>
        <tbody>
          ${sortedAttempts.map((a) => {
            const pct = a.maxScore > 0 ? Math.round((a.score / a.maxScore) * 100) : 0;
            return `<tr>
              <td>${a.createdAt ? new Date(a.createdAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : ''}</td>
              <td>${escapeHtml(a.courseTitle || '')}</td>
              <td>${escapeHtml(a.topicTitle || '')}</td>
              <td style="text-align:right;font-weight:700;color:${pctColor(pct)};">${a.score}/${a.maxScore} (${pct}%)</td>
            </tr>`;
          }).join('')}
        </tbody>
      </table></div>
    `;
  }

  function renderGrid() {
    monthLabel.textContent = new Date(Date.UTC(viewYear, viewMonth, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    const firstWeekday = new Date(Date.UTC(viewYear, viewMonth, 1)).getUTCDay();
    const daysInMonth = new Date(Date.UTC(viewYear, viewMonth + 1, 0)).getUTCDate();
    const todayKey = dateKeyFor(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate());

    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push('<div class="cal-cell cal-cell-empty"></div>');
    for (let d = 1; d <= daysInMonth; d++) {
      const key = dateKeyFor(viewYear, viewMonth, d);
      const dayAttempts = byDate.get(key) || [];
      const best = dayAttempts.reduce((max, a) => {
        const pct = a.maxScore > 0 ? (a.score / a.maxScore) * 100 : 0;
        return Math.max(max, pct);
      }, -1);
      const hasAttempts = dayAttempts.length > 0;
      cells.push(`
        <button type="button" class="cal-cell ${hasAttempts ? 'cal-cell-has-score' : ''} ${key === selectedKey ? 'cal-cell-selected' : ''} ${key === todayKey ? 'cal-cell-today' : ''}" data-date-key="${key}">
          <span class="cal-cell-daynum">${d}</span>
          ${hasAttempts ? `<span class="cal-cell-badge" style="background:${pctColor(best)};">${dayAttempts.length}</span>` : ''}
        </button>
      `);
    }
    grid.innerHTML = `
      <div class="cal-weekdays">${WEEKDAYS.map((w) => `<div>${w}</div>`).join('')}</div>
      <div class="cal-days">${cells.join('')}</div>
    `;
    grid.querySelectorAll('[data-date-key]').forEach((btn) => {
      btn.addEventListener('click', () => {
        renderDayDetail(btn.dataset.dateKey);
        renderGrid();
      });
    });
  }

  container.querySelector('#cal-prev').addEventListener('click', () => {
    viewMonth -= 1;
    if (viewMonth < 0) { viewMonth = 11; viewYear -= 1; }
    renderGrid();
  });
  container.querySelector('#cal-next').addEventListener('click', () => {
    viewMonth += 1;
    if (viewMonth > 11) { viewMonth = 0; viewYear += 1; }
    renderGrid();
  });

  renderGrid();
  renderDayDetail(selectedKey);
}
